/**
 * Page Accidentologie : liste triable/filtrable/exportable des incidents,
 * accidents et presque-accidents remontés (source Icare, feuille dédiée).
 * Les cartes KPI sont cliquables : elles appliquent un filtre rapide sur le
 * tableau ci-dessous (clic à nouveau pour revenir à la vue complète).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { ExternalLink } from "lucide-react";
import { dashboardApi } from "@/lib/api";
import { useFilters } from "@/contexts/FilterContext";
import useQuickFilter from "@/hooks/useQuickFilter";
import DataTable from "@/components/tables/DataTable";
import CategoryPieChart from "@/components/charts/CategoryPieChart";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { PAGES, CHARTS } from "@/constants/testIds";

const COLUMNS = [
  { key: "id", label: "Numéro" },
  { key: "date", label: "Date" },
  { key: "site", label: "Site" },
  { key: "type_incident", label: "Type d'incident" },
  { key: "critical", label: "Critique" },
  { key: "classification", label: "Classification" },
  { key: "name", label: "Personne" },
  { key: "segment", label: "Segment" },
  { key: "environment", label: "Environnement" },
  { key: "status", label: "Statut" },
  { key: "investigation_closed", label: "Enquête" },
  { key: "description", label: "Description", width: 320 },
];

const KPI_TONE = { slate: "text-slate-700", red: "text-red-600", blue: "text-[#0B5394]", orange: "text-amber-600" };

// Couleurs dédiées demandées par l'utilisateur pour le camembert de
// classification des blessures/maladies enregistrables (au lieu de la
// palette générique cyclique) : rouge vif = LTI, orange = MWD, jaune = MI,
// vert clair = FA.
const CLASSIFICATION_COLORS = {
  "LTI - Incident avec arrêt de travail": "#EF4444",
  "MWD - Incident avec adaptation de poste de travail": "#F97316",
  "MI – Incident médical": "#FACC15",
  "FA - Incident de premiers secours": "#86EFAC",
};

const KPI_FILTERS = {
  critical: (a) => a.critical,
  // "Avec arrêt (LTI)" ne compte que les LTI ENREGISTRABLES (chiffre officiel) -
  // les LTI non enregistrables sont exclues du comptage principal.
  lti: (a) => a.classification && a.classification.includes("LTI") && a.recordable,
  near_miss: (a) => a.type_incident.includes("Presque-accident") || a.type_incident.includes("Matériels / Biens"),
  open_investigations: (a) => !a.investigation_closed && a.status !== "Closed",
};

function KpiCard({ testId, label, value, subtext, tone, active, onClick }) {
  return (
    <Card
      data-testid={testId}
      onClick={onClick}
      className={`cursor-pointer border-slate-200 p-3 transition hover:border-[#0B5394]/40 hover:shadow-md ${
        active ? "border-[#0B5394] ring-2 ring-[#0B5394]/25" : ""
      }`}
    >
      <CardContent className="p-0">
        <p className="text-xs text-slate-500">{label}</p>
        <p className={`text-xl font-bold tabular-nums ${KPI_TONE[tone]}`}>{value}</p>
        {subtext && <p className="mt-0.5 text-[11px] leading-tight text-slate-500">{subtext}</p>}
      </CardContent>
    </Card>
  );
}

export default function AccidentsPage() {
  const { filters, refreshKey } = useFilters();
  const [data, setData] = useState(null);
  const { active: activeFilter, toggle: toggleQuickFilter, clear: clearQuickFilter, apply: applyQuickFilter } = useQuickFilter(KPI_FILTERS);
  const tableRef = useRef(null);
  const prevFiltersKeyRef = useRef(null);

  useEffect(() => {
    dashboardApi.getAccidents(filters).then(({ data }) => setData(data));
    // Un changement RÉEL de filtre global réinitialise le filtre KPI actif -
    // mais pas au montage initial (ne doit pas écraser un filtre rapide
    // demandé via l'URL ?qf=critical, lien direct depuis le Dashboard), et
    // pas non plus lors d'un ré-exécution de cet effet avec des filtres
    // identiques (ex: double-appel des effects en React.StrictMode en dev,
    // ou mise à jour interne de FilterContext qui ne change pas les valeurs
    // effectives) - on compare donc un instantané des valeurs, pas juste la
    // référence de l'objet `filters`.
    const key = `${JSON.stringify(filters)}|${refreshKey}`;
    if (prevFiltersKeyRef.current !== null && prevFiltersKeyRef.current !== key) {
      clearQuickFilter();
    }
    prevFiltersKeyRef.current = key;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, refreshKey]);

  const filteredItems = data ? applyQuickFilter(data.items) : [];

  const injuryClassificationData = useMemo(() => {
    if (!data) return [];
    const counts = {};
    data.items
      .filter((a) => a.type_incident.includes("Blessure/maladie") && a.recordable)
      .forEach((a) => {
        const key = a.classification || "Non classé";
        counts[key] = (counts[key] || 0) + 1;
      });
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [data]);

  const relatedToData = useMemo(() => {
    if (!data) return [];
    const counts = {};
    data.items.forEach((a) => {
      // "Cet incident est lié à" est un champ à choix multiples aplati en une
      // chaîne séparée par des virgules (ex: "S - Carburants..., E - Émissions...") :
      // on décompose pour compter chaque catégorie atomique séparément.
      const parts = a.related_to ? a.related_to.split(",").map((s) => s.trim()).filter(Boolean) : ["Non renseigné"];
      parts.forEach((key) => {
        counts[key] = (counts[key] || 0) + 1;
      });
    });
    const sorted = Object.entries(counts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
    // Trop de catégories distinctes rendent le camembert illisible : on
    // garde les 6 plus fréquentes + un "Autres".
    const TOP_N = 6;
    if (sorted.length <= TOP_N) return sorted;
    const top = sorted.slice(0, TOP_N);
    const autresValue = sorted.slice(TOP_N).reduce((sum, e) => sum + e.value, 0);
    return [...top, { name: "Autres", value: autresValue }];
  }, [data]);

  const handleChipClick = (key) => {
    if (key === null) clearQuickFilter();
    else toggleQuickFilter(key);
    tableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const renderCell = (col, row) => {
    if (col.key === "id") {
      return row.link ? (
        <a
          href={row.link}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          data-testid={`accidents-incident-link-${row.id}`}
          className="inline-flex items-center gap-1 font-medium text-[#0B5394] hover:underline"
        >
          {row.id} <ExternalLink className="h-3 w-3" />
        </a>
      ) : (
        row.id
      );
    }
    if (col.key === "critical") {
      return row.critical ? (
        <Badge className="border-none bg-red-100 text-red-700">Oui</Badge>
      ) : (
        <span className="text-slate-400">Non</span>
      );
    }
    if (col.key === "classification") {
      return row.classification ? (
        <Badge variant="outline" className="border-slate-300 text-slate-600">{row.classification}</Badge>
      ) : (
        <span className="text-slate-400">-</span>
      );
    }
    if (col.key === "name") {
      return row.name || <span className="text-slate-400">-</span>;
    }
    if (col.key === "status") {
      return (
        <Badge variant="outline" className={row.status === "Closed" ? "border-emerald-300 text-emerald-700" : "border-amber-300 text-amber-700"}>
          {row.status}
        </Badge>
      );
    }
    if (col.key === "investigation_closed") {
      return row.investigation_closed ? (
        <Badge className="border-none bg-emerald-100 text-emerald-700">Terminée</Badge>
      ) : (
        <Badge className="border-none bg-amber-100 text-amber-700">En cours</Badge>
      );
    }
    return undefined;
  };

  if (!data) return <Skeleton className="h-96 rounded-lg" />;

  const { summary } = data;

  return (
    <div data-testid={PAGES.accidents} className="space-y-4">
      <h1 className="text-xl font-bold text-slate-800">Accidentologie</h1>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        <KpiCard
          testId="accidents-kpi-total"
          label="Total"
          value={summary.total}
          subtext={`dont ${summary.injury_count} Blessure/maladie`}
          tone="slate"
          active={activeFilter === null}
          onClick={() => handleChipClick(null)}
        />
        <KpiCard
          testId="accidents-kpi-critical"
          label="Critiques"
          value={summary.critical}
          subtext={`${summary.critical_investigation_closed_pct}% enquêtes terminées`}
          tone="red"
          active={activeFilter === "critical"}
          onClick={() => handleChipClick("critical")}
        />
        <KpiCard
          testId="accidents-kpi-lti"
          label="Avec arrêt (LTI)"
          value={summary.lti_recordable}
          subtext={summary.lti_non_recordable > 0 ? `${summary.lti_non_recordable} non enregistrable(s) exclue(s)` : "Toutes enregistrables"}
          tone="orange"
          active={activeFilter === "lti"}
          onClick={() => handleChipClick("lti")}
        />
        <KpiCard
          testId="accidents-kpi-near-miss"
          label="Presque-accidents"
          value={summary.near_miss}
          subtext="Presque-accidents + Matériels/Biens"
          tone="blue"
          active={activeFilter === "near_miss"}
          onClick={() => handleChipClick("near_miss")}
        />
        <KpiCard
          testId="accidents-kpi-open-investigations"
          label="Enquêtes en cours"
          value={summary.open_investigations}
          tone="orange"
          active={activeFilter === "open_investigations"}
          onClick={() => handleChipClick("open_investigations")}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <CategoryPieChart
          testId={CHARTS.accidentsInjuryPie}
          title="Blessures/maladies enregistrables par classification"
          data={injuryClassificationData}
          colorMap={CLASSIFICATION_COLORS}
        />
        <CategoryPieChart
          testId={CHARTS.accidentsRelatedToPie}
          title="Répartition par catégorie de risque (cet incident est lié à)"
          data={relatedToData}
        />
      </div>

      <div ref={tableRef}>
        <DataTable testId="accidents-table" title="Accidentologie" columns={COLUMNS} data={filteredItems} renderCell={renderCell} />
      </div>
    </div>
  );
}
